import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "../../lib/analytics";
import { useDemoState } from "../../lib/demoState";

const DEMOS = {
  "Find a mentor": {
    intent: "I need a Flutter mentor.",
    understanding: ["Skill: Flutter", "Goal: mentorship", "Level: intermediate", "Format: weekly sessions"],
    actions: ["3 verified mentors matched", "Tara — 4 yrs Flutter, 12 mentees", "Intro conversation drafted", "Session slots suggested", "Tracked in My Happenings"],
  },
  "Plan a weekend": {
    intent: "What can I do this weekend?",
    understanding: ["Time: Sat–Sun", "Location: near you", "Mood: social + outdoors", "Budget: flexible"],
    actions: ["Weekend Photo Walk — Sat morning", "Sunday Product Breakfast — 12 going", "Evening Cycling Group — Sun 5 PM", "Friends who are free surfaced", "One-tap booking ready"],
  },
  "Start a community": {
    intent: "I want to start a local founder community.",
    understanding: ["Type: community", "Audience: founders", "Area: your city", "Cadence: monthly"],
    actions: ["Community SLM (HumnAi) structured", "17 nearby founders suggested", "First meetup template created", "Venue options nearby", "Workspace opened"],
  },
};

export const AIDemoOverlay = ({ open, onClose, initialPrompt }) => {
  const [prompt, setPrompt] = useState(null);
  const [custom, setCustom] = useState("");
  const [stage, setStage] = useState(0);
  const closeRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || null);
      setStage(initialPrompt ? 1 : 0);
      setCustom("");
      track("humn_ai_demo_started");
      setTimeout(() => closeRef.current?.focus(), 50);
    }
  }, [open, initialPrompt]);

  useEffect(() => {
    if (!prompt) return;
    setStage(1);
    const t1 = setTimeout(() => setStage(2), 1400);
    const t2 = setTimeout(() => { setStage(3); track("humn_ai_demo_completed"); }, 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [prompt]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const demo = prompt ? (DEMOS[prompt] || { intent: prompt, understanding: ["Intent parsed", "Context: your interests", "Location: near you", "Timing: flexible"], actions: ["Relevant people surfaced", "Matching SLMs (HumnAi) found", "Suggested next step prepared", "Workspace ready to open"] }) : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog" aria-modal="true" aria-label="HUMN AI product demonstration"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] overflow-y-auto"
          style={{ background: "rgba(6,3,14,0.9)", backdropFilter: "blur(28px)", WebkitBackdropFilter: "blur(28px)" }}
          data-lenis-prevent
          data-testid="ai-demo-overlay"
        >
          <div className="min-h-full flex flex-col max-w-2xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <span className="eyebrow eyebrow-dark flex items-center gap-2">
                <span className="live-dot" /> Product demonstration
              </span>
              <button ref={closeRef} data-testid="ai-demo-close-btn" aria-label="Close demo" onClick={onClose} className="btn-pill text-white/60 hover:text-white border border-white/20 px-4 py-1.5 text-sm">
                Close
              </button>
            </div>

            <div className="flex-1 flex flex-col justify-center py-14">
              {!prompt ? (
                <div>
                  <h3 className="h-display h-md text-white">What would you like to do?</h3>
                  <div className="mt-8 space-y-3">
                    {Object.keys(DEMOS).map((p) => (
                      <button
                        key={p}
                        data-testid={`ai-demo-prompt-${p.toLowerCase().replace(/\s/g, "-")}`}
                        onClick={() => { setPrompt(p); track("humn_ai_demo_prompt_selected", { prompt: p }); }}
                        className="btn-pill block w-full text-left px-6 py-4 border border-white/15 text-white/85 hover:bg-white/10 text-[15px]"
                      >
                        {DEMOS[p].intent}
                      </button>
                    ))}
                  </div>
                  <form
                    className="mt-6 flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (custom.trim()) { setPrompt(custom.trim()); track("humn_ai_demo_prompt_selected", { prompt: "custom" }); }
                    }}
                  >
                    <label htmlFor="ai-demo-custom" className="sr-only">Type your own intention</label>
                    <input
                      id="ai-demo-custom"
                      data-testid="ai-demo-custom-input"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      placeholder="Or type your own intention…"
                      className="flex-1 bg-white/5 border border-white/15 rounded-full px-5 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-white/40"
                    />
                    <button type="submit" data-testid="ai-demo-custom-submit" className="btn-pill px-5 py-3 text-sm font-semibold text-white" style={{ background: "linear-gradient(120deg,#8b5cf6,#ff2e86)" }}>
                      Try
                    </button>
                  </form>
                </div>
              ) : (
                <div className="space-y-8">
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="ml-auto max-w-md bg-white text-black rounded-3xl rounded-tr-lg px-6 py-4 text-[15px] w-fit">
                    {demo.intent}
                  </motion.div>

                  {stage >= 2 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <p className="eyebrow eyebrow-dark mb-3">HUMN AI understood</p>
                      <div className="flex flex-wrap gap-2">
                        {demo.understanding.map((u, i) => (
                          <motion.span key={u} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.15 }} className="font-mono-ui text-[11px] px-3 py-1.5 rounded-full border border-white/20 text-white/70">
                            {u}
                          </motion.span>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {stage >= 3 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <p className="eyebrow eyebrow-dark mb-3">Turned into action</p>
                      <div className="space-y-2.5">
                        {demo.actions.map((a, i) => (
                          <motion.div key={a} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.18 }} className="flex items-center gap-3 rounded-2xl px-5 py-3.5" style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.28)" }}>
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--pink)" }} />
                            <span className="text-white/85 text-sm">{a}</span>
                          </motion.div>
                        ))}
                      </div>
                      <button data-testid="ai-demo-try-another-btn" onClick={() => { setPrompt(null); setStage(0); }} className="btn-pill mt-8 px-6 py-2.5 border border-white/20 text-white/80 hover:bg-white/10 text-sm">
                        Try another intention
                      </button>
                    </motion.div>
                  )}

                  {stage === 1 && (
                    <div className="flex items-center gap-2 text-white/50 text-sm">
                      <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity }}>HUMN AI is thinking</motion.span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-white/30 text-xs text-center">This is a simulated product demonstration. No data leaves your browser.</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ComposeBar = ({ onOpen, dark = false, className = "" }) => {
  const examples = ["Meet people building something interesting", "Find a mentor", "Join a community", "Plan an experience", "Teach a skill", "Start a project", "Create an SLM (HumnAi)"];
  const [i, setI] = useState(0);
  const { setIntent } = useDemoState();
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % examples.length), 3600);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setIntent(examples[i]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);
  return (
    <button
      data-testid="compose-bar-trigger"
      aria-label="Open HUMN AI demo — what would you like to do today?"
      onClick={onOpen}
      className={`compose-ring group flex items-center justify-between gap-4 px-5 py-3.5 w-full max-w-md text-left ${className}`}
    >
      <span className="w-9 h-9 rounded-full ai-orb flex items-center justify-center shrink-0 glow-breathe">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M12 2l1.8 5.6L19 9l-5.2 1.4L12 16l-1.8-5.6L5 9l5.2-1.4L12 2z" /></svg>
      </span>
      <span className="text-left overflow-hidden flex-1">
        <span className="block text-[11px] tx-3">What would you like to do today?</span>
        <span className="block h-6 relative">
          <AnimatePresence mode="wait">
            <motion.span
              key={i}
              initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -14, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
              className="absolute left-0 text-[14px] font-medium tracking-tight tx-1 whitespace-nowrap"
            >
              {examples[i]}
            </motion.span>
          </AnimatePresence>
        </span>
      </span>
      <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ background: "linear-gradient(135deg, var(--purple), var(--pink))" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      </span>
    </button>
  );
};
